import { createSlice, PayloadAction } from '@reduxjs/toolkit'
// enableMapSet is now called in index.tsx
import { sanitizeId } from '../util'
import { BackgroundMode } from '../../../types/BackgroundMode'
import { UserData, UserRole } from '../types/user'
import { PlayerStatus } from '../../../types/PlayerStatus'

// NOTE: phaserGame import removed - Phaser is now lazy loaded
// See PhaserGame.ts for the lazy loading implementation

// Define connection status enum for player connection state
export enum ConnectionStatus {
  OFFLINE = 'offline',
  LOADING = 'loading',
  ONLINE = 'online',
}

export function getInitialBackgroundMode() {
  const currentHour = new Date().getHours()
  return currentHour > 6 && currentHour <= 18 ? BackgroundMode.DAY : BackgroundMode.NIGHT
}

interface UserState {
  loggedIn: boolean
  authenticated: boolean
  sessionId: string
  backgroundMode: BackgroundMode
  playerNameMap: Record<string, string>
  /** Session id → Parse user id of remote players, so lists can tell people apart by id, not name. */
  playerUserIdMap: Record<string, string>
  userData: UserData | null
  userRole: UserRole | null
  connectionStatus: ConnectionStatus // Add connection status to track player loading state
  status: PlayerStatus // Add status here
  statusText: string // Custom status text (max 35 chars)
  statusIcon: string // Emoji icon for the status
  playerStatusTextMap: Record<string, string> // Other players' custom status texts
  playerStatusIconMap: Record<string, string> // Other players' status icons
  playerPresenceMap: Record<string, PlayerStatus> // Other players' Online / Away / Do Not Disturb
  helpTooltipVisible: boolean // Add help tooltip visibility state
  menuOpen: boolean // Add menu open state
  profileDialogOpen: boolean // Profile edit/setup dialog open (hides OptimizedLoadingScreen)
  /** LiveKit/Colyseus media reconnect UX (nameplate + optional full-screen dialog) */
  liveKitReconnectState: {
    showPrompt: boolean
    message: string
    cooldownUntil?: number
    reconnectAttempt?: number
    maxAttempts?: number
    /** Prominent modal with Reconnect Now / Leave room */
    showReconnectionDialog?: boolean
  }
}

const initialState: UserState = {
  loggedIn: false,
  authenticated: false,
  sessionId: '',
  backgroundMode: BackgroundMode.DAY,
  playerNameMap: {},
  playerUserIdMap: {},
  userData: null as UserData | null,
  userRole: null as UserRole | null,
  connectionStatus: ConnectionStatus.OFFLINE, // Default to offline
  status: PlayerStatus.ONLINE, // Add initial status
  statusText: '', // Default empty custom status
  statusIcon: '', // Default no status icon
  playerStatusTextMap: {}, // Other players' custom status texts
  playerStatusIconMap: {}, // Other players' status icons
  playerPresenceMap: {},
  // Collapsed until the first room join decides: HelpTooltip expands it on an account's first day
  helpTooltipVisible: false,
  menuOpen: false, // Default to closed
  profileDialogOpen: false,
  liveKitReconnectState: {
    showPrompt: false,
    message: '',
    showReconnectionDialog: false,
  },
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.loggedIn = action.payload
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.authenticated = action.payload
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload
    },
    toggleBackgroundMode: (state) => {
      state.backgroundMode =
        state.backgroundMode === BackgroundMode.DAY ? BackgroundMode.NIGHT : BackgroundMode.DAY
    },
    setPlayerName: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const { id, name } = action.payload
      state.playerNameMap[id] = name
    },
    setPlayerUserId: (state, action: PayloadAction<{ id: string; userId: string }>) => {
      const { id, userId } = action.payload
      if (!state.playerUserIdMap) state.playerUserIdMap = {}
      if (userId) state.playerUserIdMap[id] = userId
    },
    removePlayerName: (state, action: PayloadAction<string>) => {
      delete state.playerNameMap[action.payload]
      if (state.playerUserIdMap) delete state.playerUserIdMap[action.payload]
    },
    clearPlayerNames: (state) => {
      state.playerNameMap = {}
      state.playerUserIdMap = {}
    },
    setUserData: (state, action: PayloadAction<UserData | null>) => {
      state.userData = action.payload
      if (action.payload) {
        state.userRole = action.payload.role
      } else {
        state.userRole = null
      }
    },
    // New action to update connection status
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload
    },
    // Add the new reducer for player status
    setPlayerStatus: (state, action: PayloadAction<PlayerStatus>) => {
      state.status = action.payload
    },
    // Custom status text for the current player
    setStatusText: (state, action: PayloadAction<string>) => {
      state.statusText = action.payload.substring(0, 35)
    },
    // Custom status icon (emoji) for the current player
    setStatusIcon: (state, action: PayloadAction<string>) => {
      state.statusIcon = action.payload
    },
    // Custom status text for other players
    setPlayerStatusText: (state, action: PayloadAction<{ id: string; statusText: string }>) => {
      const { id, statusText } = action.payload
      if (statusText) {
        state.playerStatusTextMap[id] = statusText
      } else {
        delete state.playerStatusTextMap[id]
      }
    },
    // Custom status icon for other players
    setPlayerStatusIcon: (state, action: PayloadAction<{ id: string; statusIcon: string }>) => {
      const { id, statusIcon } = action.payload
      if (statusIcon) {
        state.playerStatusIconMap[id] = statusIcon
      } else {
        delete state.playerStatusIconMap[id]
      }
    },
    setOtherPlayerPresence: (state, action: PayloadAction<{ id: string; status: PlayerStatus }>) => {
      state.playerPresenceMap[action.payload.id] = action.payload.status
    },
    removePlayerStatusText: (state, action: PayloadAction<string>) => {
      delete state.playerStatusTextMap[action.payload]
      delete state.playerStatusIconMap[action.payload]
      delete state.playerPresenceMap[action.payload]
    },
    /**
     * Purge all player-related maps (names, status texts, status icons).
     * Call when leaving a room to prevent unbounded growth across sessions.
     */
    clearPlayerMaps: (state) => {
      state.playerNameMap = {}
      state.playerStatusTextMap = {}
      state.playerStatusIconMap = {}
      state.playerPresenceMap = {}
    },
    // Add reducer for help tooltip visibility
    setHelpTooltipVisible: (state, action: PayloadAction<boolean>) => {
      state.helpTooltipVisible = action.payload
    },
    // Add reducer for menu visibility
    setMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.menuOpen = action.payload
    },
    setProfileDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.profileDialogOpen = action.payload
    },
    setLiveKitReconnectState: (
      state,
      action: PayloadAction<Partial<UserState['liveKitReconnectState']> & { showPrompt?: boolean; message?: string }>
    ) => {
      state.liveKitReconnectState = {
        ...state.liveKitReconnectState,
        ...action.payload,
      }
    },
  },
})

export const {
  setLoggedIn,
  setAuthenticated,
  setSessionId,
  toggleBackgroundMode,
  setPlayerName,
  removePlayerName,
  setPlayerUserId,
  clearPlayerNames,
  setUserData,
  setConnectionStatus, // Export the new action
  setPlayerStatus, // Export the new action
  setStatusText, // Custom status text
  setStatusIcon, // Custom status icon (emoji)
  setPlayerStatusText, // Other players' status text
  setPlayerStatusIcon, // Other players' status icon
  setOtherPlayerPresence,
  removePlayerStatusText, // Remove player status text/icon on leave
  clearPlayerMaps, // Purge all player maps on room leave
  setHelpTooltipVisible, // Export the help tooltip action
  setMenuOpen, // Export the menu action
  setProfileDialogOpen,
  setLiveKitReconnectState,
} = userSlice.actions

export default userSlice.reducer
